import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CreateCSSProperties } from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import color from 'color';
import React, { CSSProperties } from 'react';
import {
    accentVariant,
    black,
    dangerVariant,
    divider,
    headingVariant,
    info,
    lightVariant,
    primary,
    successVariant,
    warningVariant,
    white
} from '../Styles/Colours';

function tint($color: string, $percentage: number) {
    return color($color)
        .mix(color(white), $percentage / 100)
        .string();
}

function shade($color: string, $percentage: number) {
    return color($color)
        .mix(color(black), $percentage / 100)
        .string();
}

function translucent($color: string, $percentage: number) {
    return color($color)
        .alpha($percentage / 100)
        .string();
}

export type ButtonProps = {
    children?: React.ReactNode;
    mode?: ButtonMode;
    variant?: ButtonVariant;
    size?: ButtonSize;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    testId: string;
    color?: string;
    disabled?: boolean;
    width?: string | number;
    style?: React.CSSProperties;
    iconOnly?: boolean;
    squareIcon?: boolean;
    type?: 'button' | 'submit' | 'reset' | undefined;
    className?: string;
    title?: string;
    noShadowOnHover?: boolean;
    subtleShadeOnHover?: boolean;
    smallText?: boolean;
    tighterIcons?: boolean;
    buttonGroup?: ButtonGroupStyle;
    borderColour?: string;
    tooltip?: string;
    onClick?(event: React.MouseEvent): void;
};

export enum ButtonMode {
    solid = 'solid',
    border = 'border',
    text = 'text',
    glow = 'glow'
}

export enum ButtonVariant {
    primary = 'primary',
    accent = 'accent',
    light = 'light',
    info = 'info',
    success = 'success',
    warning = 'warning',
    danger = 'danger',
    heading = 'heading'
}

export enum ButtonSize {
    small = 'small',
    medium = 'medium',
    large = 'large'
}

export enum ButtonGroupStyle {
    Left,
    Center,
    Right
}

type ButtonColors = {
    backgroundColor?: string;
    color?: string;
    border?: string;
    borderRadius?: string;
    borderRight?: string;
    hover: {
        backgroundColor?: string;
        borderColor?: string;
    };
    active: {
        backgroundColor?: string;
    };
    disabled: {
        backgroundColor?: string;
        borderColor?: string;
        color?: string;
        border?: string;
        borderRight?: string;
        hover: {
            backgroundColor?: string;
            borderColor?: string;
            borderRight?: string;
        };
    };
};

const buttonColors = (props: ButtonProps) => (
    base: string,
    solidText: string,
    borderText: string,
    shaded: boolean
): ButtonColors => {
    let result: ButtonColors = {
        backgroundColor: base,
        color: solidText,
        hover: {
            backgroundColor: tint(base, 20)
        },
        active: {
            backgroundColor: tint(base, 20)
        },
        disabled: {
            backgroundColor: tint(base, 50),
            color: tint(solidText, 50),
            hover: {
                backgroundColor: tint(base, 50),
                borderColor: base
            }
        }
    };

    let buttonMode = props.mode ?? ButtonMode.solid;
    let buttonVariant = props.variant ?? ButtonVariant.primary;

    if (buttonMode === ButtonMode.solid) {
        result = {
            backgroundColor: base,
            color: solidText,
            hover: {
                backgroundColor: tint(base, 20)
            },
            active: {
                backgroundColor: tint(base, 20)
            },
            disabled: {
                backgroundColor: tint(base, 50),
                color: tint(solidText, 50),
                hover: {
                    backgroundColor: tint(base, 50),
                    borderColor: base
                }
            }
        };
    } else if (buttonMode === ButtonMode.border) {
        result = {
            backgroundColor: white,
            color: borderText,
            border: `1px solid ${tint(base, 10)}`,
            hover: {
                backgroundColor: shaded ? shade(base, 5) : tint(base, 90)
            },
            active: {
                backgroundColor: shaded ? shade(base, 5) : tint(base, 90)
            },
            disabled: {
                borderColor: tint(base, 50),
                color: tint(solidText, 50),
                hover: {
                    backgroundColor: white,
                    borderColor: tint(base, 50)
                }
            }
        };
    } else if (buttonMode === ButtonMode.text) {
        result = {
            backgroundColor: 'transparent',
            color: borderText,
            hover: {
                backgroundColor: props.subtleShadeOnHover
                    ? translucent(base, 10)
                    : shaded
                    ? shade(base, 5)
                    : tint(base, 90)
            },
            active: {
                backgroundColor: props.subtleShadeOnHover
                    ? translucent(base, 10)
                    : shaded
                    ? shade(base, 5)
                    : tint(base, 90)
            },
            disabled: {
                borderColor: tint(base, 50),
                color: tint(borderText, 50),
                hover: {
                    backgroundColor: 'transparent',
                    borderColor: tint(base, 50)
                }
            }
        };
    } else if (buttonMode === ButtonMode.glow) {
    }
    if (
        buttonVariant === ButtonVariant.light &&
        (buttonMode === ButtonMode.border || buttonMode === ButtonMode.solid)
    ) {
        result.border = `1px solid ${divider}`;
        result.backgroundColor = base;

        result.hover.borderColor = divider;
        result.hover.backgroundColor = tint(divider, 50);

        result.disabled.border = `1px solid ${divider}`;
        result.disabled.hover.borderColor = divider;
    }

    if (props.buttonGroup === ButtonGroupStyle.Left) {
        result.borderRadius = `2px 0 0 2px`;
        result.borderRight = 'none';
        result.disabled.borderRight = 'none';
    } else if (props.buttonGroup === ButtonGroupStyle.Center) {
        result.borderRadius = '0';
        result.borderRight = 'none';
        result.disabled.borderRight = 'none';
    } else if (props.buttonGroup === ButtonGroupStyle.Right) {
        result.borderRadius = '0 2px 2px 0';
    }

    return result;
};

const useStyles = makeStyles({
    button: (props: ButtonProps) => {
        let buttonColorStyles: ButtonColors = buttonColors(props)(primary, white, primary, false);

        if (props.variant === ButtonVariant.primary) {
            buttonColorStyles = buttonColors(props)(primary, white, primary, false);
        } else if (props.variant === ButtonVariant.accent) {
            buttonColorStyles = buttonColors(props)(accentVariant, white, accentVariant, false);
        } else if (props.variant === ButtonVariant.light) {
            buttonColorStyles = buttonColors(props)(lightVariant, primary, primary, true);
        } else if (props.variant === ButtonVariant.info) {
            buttonColorStyles = buttonColors(props)(info, white, info, false);
        } else if (props.variant === ButtonVariant.success) {
            buttonColorStyles = buttonColors(props)(successVariant, white, successVariant, false);
        } else if (props.variant === ButtonVariant.warning) {
            buttonColorStyles = buttonColors(props)(warningVariant, '#503407', warningVariant, false);
        } else if (props.variant === ButtonVariant.danger) {
            buttonColorStyles = buttonColors(props)(dangerVariant, white, dangerVariant, false);
        } else if (props.variant === ButtonVariant.heading) {
            buttonColorStyles = buttonColors(props)(headingVariant, white, headingVariant, false);
        }

        let padding = '0 16px';
        let height = 40;
        let fontSize = 16;
        let minWidth = '112';
        let width;
        let borderRadius = '2';

        if (props.smallText) {
            fontSize = 14;
        } else if (props.size === ButtonSize.small) {
            padding = '0 12px';
            height = 32;
            fontSize = 14;
            minWidth = '100';
        } else if (props.size === ButtonSize.large) {
            height = 48;
        }

        if (props.iconOnly) {
            borderRadius = '50%';
            width = 40;
            minWidth = 'auto';

            if (props.size === ButtonSize.small) {
                width = 32;
            } else if (props.size === ButtonSize.large) {
                height = 48;
            }

            if (props.squareIcon) {
                borderRadius = 'unset';
            }
        }

        return {
            padding,
            border: 'transparent',
            backgroundColor: primary,
            color: white,
            cursor: `pointer`,
            transition: `background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
            fontFamily:
                "'Source Sans Pro', -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            fontSize,
            minWidth,
            borderRadius,
            height,
            width,
            outline: `0`,
            boxShadow: 'none',
            ...buttonColorStyles,
            '&:hover': {
                ...buttonColorStyles.hover,
                boxShadow: props.noShadowOnHover
                    ? `none`
                    : `0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),0px 1px 10px 0px rgba(0, 0, 0, 0.12)`
            },
            '&:active': {
                ...buttonColorStyles.active,
                boxShadow: 'none'
            },
            '&:disabled': {
                cursor: `default`,
                ...buttonColorStyles.disabled,
                '&:hover': {
                    ...buttonColorStyles.disabled.hover,
                    boxShadow: 'none'
                }
            }
        };
    },
    label: {
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'center',
        alignItems: 'center'
    },
    children: (props: ButtonProps) => {
        let styles: CreateCSSProperties<ButtonProps> = {
            flex: '1 1 100%',
            display: 'flex',
            justifyContent: 'center'
        };

        if (props.tighterIcons) {
            styles.flexGrow = 0;
            styles.flexBasis = 'auto;';
        }

        return styles;
    },
    startIcon: {
        marginRight: 8,
        display: 'flex',
        'button:disabled &': {
            opacity: 0.5
        }
    },
    endIcon: {
        marginLeft: 8,
        display: 'flex',
        'button:disabled &': {
            opacity: 0.5
        }
    },
    buttonGroupLeft: {
        borderRadius: '2px 0 0 2px',
        borderRight: 'none',
        '&:disabled': {
            borderRight: 'none'
        }
    },
    buttonGroupCentre: {
        borderRadius: 0,
        borderRight: 'none',
        '&:disabled': {
            borderRight: 'none'
        }
    },
    buttonGroupRight: {
        borderRadius: '0 2px 2px 0'
    }
});

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const { disabled, variant, size, ...otherProps } = props;
    const cssInJsClasses = useStyles(props);

    const style: CSSProperties = {
        ...otherProps.style,
        width: otherProps.width,
        minWidth: otherProps.width
    };

    if (otherProps.borderColour) {
        style.borderColor = otherProps.borderColour;
    }

    const classes = classNames(cssInJsClasses.button, otherProps.className, {
        [cssInJsClasses.buttonGroupLeft]: otherProps.buttonGroup === ButtonGroupStyle.Left,
        [cssInJsClasses.buttonGroupCentre]: otherProps.buttonGroup === ButtonGroupStyle.Center,
        [cssInJsClasses.buttonGroupRight]: otherProps.buttonGroup === ButtonGroupStyle.Right
    });

    const startIcon = !!otherProps.startIcon && (
        <span className={cssInJsClasses.startIcon}>{otherProps.startIcon}</span>
    );
    const endIcon = !!otherProps.endIcon && <span className={cssInJsClasses.endIcon}>{otherProps.endIcon}</span>;
    const testId = !!otherProps.testId ? `button_${otherProps.testId}` : null;

    const button = (
        <button
            ref={ref}
            className={classes}
            onClick={otherProps.onClick}
            style={style}
            disabled={disabled}
            data-test-id={testId}
            type={otherProps.type}
            title={otherProps.title}
        >
            <span className={cssInJsClasses.label}>
                {startIcon}
                <span className={cssInJsClasses.children}>{otherProps.children}</span>
                {endIcon}
            </span>
        </button>
    );

    return props.tooltip ? <Tooltip title={props.tooltip}>{button}</Tooltip> : button;
});
